/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect, Fragment } from 'react';
import SvgImage from '../SvgImage';
import './styles.scss';

interface ResponsiveTableProps {
  headers: {
    alwaysVisible: boolean;
    key: string;
    label: string;
    hiddenOnMobile?: boolean;
  }[];
  rows: {
    row: Record<string, string | number>;
    Table?: JSX.Element;
  }[];
  stickyCol?: JSX.Element[];
  className?: string;
  moreClassName?: string;
  headerClassName?: string;
  rowClassName?: string;
  cellClassName?: string;
  nested?: boolean;
}

const ResponsiveTable = ({
  headers,
  rows,
  stickyCol,
  className,
  moreClassName,
  headerClassName,
  rowClassName,
  cellClassName,
  nested,
}: ResponsiveTableProps) => {
  const [isExpanded, setExpanded] = useState<boolean>(false);
  const [rowExpandedStates, setRowExpandedStates] = useState<Record<number, boolean>>({});

  const toggleExpandTable = () => {
    setExpanded(!isExpanded);
  };

  const toggleExpandRow = (rowIndex: number) => {
    setRowExpandedStates({
      ...rowExpandedStates,
      [rowIndex]: !rowExpandedStates[rowIndex],
    });
  };

  useEffect(() => {
    if (rows.some((row) => row.Table)) {
      setRowExpandedStates(Object.fromEntries(Object.entries(rows.map(() => false))));
    }
  }, []);

  return (
    <div className={`responsive-table ${nested ? 'responsive-table--nested' : ''}`}>
      {!nested && (
        <div className={moreClassName || ''}>
          <button
            className="responsive-table__more"
            aria-expanded={isExpanded}
            onClick={toggleExpandTable}
          >
            Show details
          </button>
        </div>
      )}

      <div className={`responsive-table__container ${className || ''} ${isExpanded ? `${className}--full` : ''}`}>
        <table
          className={`responsive-table__table ${isExpanded ? 'responsive-table__table--full' : ''}`}
        >
          <thead>
            <tr>
              {stickyCol && (
                <td className="responsive-table__col responsive-table__row-head" />
              )}

              {headers.map((header) => (
                <th
                  key={header.key}
                  className={`
                    responsive-table__col
                    ${header.alwaysVisible ? 'responsive-table__col--permanent' : ''}
                    responsive-table__cell
                    ${header.hiddenOnMobile ? 'responsive-table__cell--hidden-mobile' : ''}
                    ${header.alwaysVisible ? 'responsive-table__cell--permanent' : ''}
                    ${headerClassName || ''}
                  `.trim()}
                  scope="col"
                >
                  {header.label}
                </th>
              ))}

              {rows.some((row) => row.Table) && (
                <th
                  className={`responsive-table__col responsive-table__cell responsive-table__cell--expand ${headerClassName || ''}`}
                  scope="col"
                >
                  <div className="sr-only">
                    expand
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ row, Table }, rowIndex) => (
              <Fragment key={`row-${rowIndex}`}>
                <tr
                  className={`
                    responsive-table__row
                    ${Table && rowExpandedStates[rowIndex] ? 'responsive-table__row-parent--expanded' : ''}
                    ${rowClassName || ''}
                  `.trim()}
                >
                  {headers.map((header, colIndex) => (
                    <React.Fragment key={`row-${rowIndex}-col-${colIndex}`}>
                      {stickyCol && colIndex === 0 && (
                        <th className={`responsive-table__row-head ${cellClassName}`} scope="row">{stickyCol[rowIndex]}</th>
                      )}
                      <td
                        className={`
                          responsive-table__cell
                          ${header.alwaysVisible ? 'responsive-table__cell--permanent' : ''}
                          ${header.hiddenOnMobile ? 'responsive-table__cell--hidden-mobile' : ''}
                          ${cellClassName || ''}
                        `.trim()}
                      >
                        {row[header.key]}
                      </td>
                    </React.Fragment>
                  ))}

                  {Table && (
                    <td
                      className={`responsive-table__cell responsive-table__cell--expand ${cellClassName || ''}`}
                    >
                      <button
                        className="responsive-table__row-expand"
                        aria-expanded={rowExpandedStates[rowIndex]}
                        onClick={() => toggleExpandRow(rowIndex)}
                      >
                        <SvgImage title="Expand row" reference="#icon-expand" />
                      </button>
                    </td>
                  )}
                </tr>
                {Table && (
                  <tr className={rowExpandedStates[rowIndex] ? 'responsive-table__row--expanded' : 'responsive-table__row--collapsed'}>
                    <td
                      className={`responsive-table__nested ${rowExpandedStates[rowIndex] ? 'responsive-table__nested--expanded' : ''}`}
                      colSpan={headers.length + (stickyCol ? 1 : 0)}
                    >
                      { Table }
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ResponsiveTable;
